import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowUpRight, Check, BookOpen } from "lucide-react";
import { PACKAGES } from "../data/content";
import { Reveal } from "./Reveal";
import { MenuPortalChooserModal } from "./MenuPortalChooserModal";
import { useAuth } from "../context/AuthContext";

// Map server "best_for" / "includes" field-names to the legacy shape used
// throughout the static fallback. Keeps the rest of the file unchanged.
const normalize = (p) => ({
  id: p.id,
  name: p.name,
  image: p.image,
  description: p.description,
  bestFor: p.best_for ?? p.bestFor ?? "",
  includes: Array.isArray(p.includes) ? p.includes : [],
});

export const CateringPackages = () => {
  const [chooserOpen, setChooserOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [livePackages, setLivePackages] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${apiBase}/api/packages`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const items = (data.items || []).map(normalize);
        if (mounted && items.length > 0) setLivePackages(items);
      } catch {
        // Network unavailable — keep static fallback.
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const renderPackages = livePackages || PACKAGES;

  const handleBrowseMenu = () => {
    const role = user?.role;
    if (role === "agency" || role === "admin") {
      navigate("/agency");
      return;
    }
    if (role === "private") {
      navigate("/menu");
      return;
    }
    setChooserOpen(true);
  };

  return (
    <section
      id="packages"
      data-testid="catering-packages-section"
      className="py-10 sm:py-12 lg:py-14 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-10">
          <div className="max-w-2xl">
            <Reveal>
              <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-brand-teal">
                Catering Menu
              </p>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-[42px] tracking-tight text-brand-charcoal leading-[1.05]">
                Catering Packages That Make Ordering Easy.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={2}>
            <p className="text-[14px] lg:text-[15px] text-brand-charcoal/70 leading-relaxed lg:max-w-sm lg:text-right">
              Choose a package, customize your proteins and sides, and let our team handle
              the food. Every order is quoted custom to your event.
            </p>
          </Reveal>
        </div>

        <div className="mt-8 lg:mt-10 grid md:grid-cols-2 gap-4 md:gap-5">
          {renderPackages.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i + 1, 4)}>
              <article
                data-testid={`package-card-${p.id}`}
                className="group flex flex-col h-full overflow-hidden rounded-2xl bg-white border border-black/5 shadow-[0_8px_30px_-12px_rgba(17,17,17,0.12)] transition-all hover:-translate-y-1 hover:shadow-[0_22px_50px_-18px_rgba(17,17,17,0.25)]"
              >
                <div className="relative h-32 md:h-36 lg:h-40 overflow-hidden">
                  <img
                    src={p.image}
                    alt={`${p.name} — catering food spread`}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/70 via-brand-charcoal/15 to-transparent" />
                  <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-2 py-0.5 shadow-sm">
                    <span className="h-1 w-1 rounded-full bg-brand-gold" />
                    <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-charcoal">
                      Quote-Based
                    </span>
                  </div>
                  <div className="absolute bottom-2.5 left-3 right-3">
                    <h3 className="font-display font-extrabold text-white text-[17px] md:text-[18px] lg:text-[20px] leading-tight tracking-tight">
                      {p.name}
                    </h3>
                  </div>
                </div>

                <div className="flex flex-col flex-1 p-4 sm:p-5">
                  <p className="text-[12.5px] text-brand-charcoal/70 leading-snug line-clamp-2">
                    {p.description}
                  </p>

                  <p className="mt-3 text-[9.5px] font-bold uppercase tracking-[0.18em] text-brand-teal">
                    Includes
                  </p>
                  <ul className="mt-1.5 grid gap-1">
                    {p.includes.slice(0, 3).map((line) => (
                      <li
                        key={line}
                        className="flex items-start gap-1.5 text-[11.5px] text-brand-charcoal/85 leading-snug"
                      >
                        <Check className="h-3 w-3 mt-[3px] shrink-0 text-brand-green" />
                        <span className="line-clamp-1">{line}</span>
                      </li>
                    ))}
                    {p.includes.length > 3 ? (
                      <li className="text-[10.5px] text-brand-charcoal/55 italic pl-[18px]">
                        + {p.includes.length - 3} more
                      </li>
                    ) : null}
                  </ul>

                  <p className="mt-3 text-[11px] text-brand-charcoal/65 leading-snug line-clamp-1">
                    <span className="font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 text-[9.5px]">
                      Best for:
                    </span>{" "}
                    {p.bestFor}
                  </p>

                  <div className="mt-auto pt-3 flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      data-testid={`package-browse-menu-${p.id}`}
                      onClick={handleBrowseMenu}
                      className="group/btn inline-flex items-center gap-1.5 rounded-full border border-brand-charcoal/15 bg-white text-brand-charcoal px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-all whitespace-nowrap"
                    >
                      <BookOpen className="h-3 w-3" />
                      Browse Menu
                    </button>
                    <Link
                      to="/quote"
                      data-testid={`package-cta-${p.id}`}
                      className="group/btn inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-all whitespace-nowrap"
                    >
                      Request Quote
                      <ArrowUpRight className="h-3 w-3 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <MenuPortalChooserModal open={chooserOpen} onOpenChange={setChooserOpen} />
    </section>
  );
};

export default CateringPackages;
